import { z } from "zod";
import {
  Axis,
  BarRounded,
  BarSeries,
  Box,
  CHART_RAMP_TOKENS,
  ChartGrid,
  ChartFrame,
  ChartTooltip,
  ScrollArea,
  Text,
  WidgetEmpty,
  WidgetSkeleton,
  XYChart,
  compactNumber,
  useCategoricalColors,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  truncateLabel,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { resolveEntityLabel } from "#app/lib/entity-visuals";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { useMostActiveUsers, type MostActiveUserRow } from "./use-most-active-users";

export const mostActiveUsersConfigSchema = z
  .looseObject({
    limit: z.int().min(3).max(20).default(8),
    mode: z.enum(["list", "bar"]).default("list"),
  });

export type MostActiveUsersConfig = z.infer<typeof mostActiveUsersConfigSchema>;

const xAccessor = (d: MostActiveUserRow) => d.count;
const yAccessor = (d: MostActiveUserRow) => resolveEntityLabel({ kind: "user", id: d.userId, user: d.user });
const ZERO_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

function MostActiveUsersList({
  rows,
  maxCount,
}: {
  rows: MostActiveUserRow[];
  maxCount: number;
}) {
  return (
    <Box direction="column" fill="flex" gap="xs">
      {/* The scrollbar is the user-visible chrome here (nothing reads the
        * scroller in JS), so ScrollArea owns it and `scrollFade` softens the
        * edges. */}
      <ScrollArea scrollFade>
        <Box direction="column" padding="sm" gap="sm">
          {rows.map((row, index) => (
            <Box key={`${row.userId}-${index}`} direction="row" align="center" gap="sm">
              <Box width={20} align="end">
                <Text size="xs" variant="muted">
                  {index + 1}
                </Text>
              </Box>
              <Box flex={1} minWidth={0}>
                <EntityTag entity={{ kind: "user", id: row.userId, user: row.user }} interactive />
              </Box>
              <Box width={84} height={8}>
                <ChartFrame ariaLabel={`${yAccessor(row)} activity proportion`} margin={ZERO_MARGIN}>
                  {({ innerWidth, innerHeight }) => {
                    const radius = innerHeight / 2;
                    const width = maxCount > 0 ? (row.count / maxCount) * innerWidth : 0;
                    return (
                      <>
                        <BarRounded
                          x={0}
                          y={0}
                          width={innerWidth}
                          height={innerHeight}
                          radius={radius}
                          all
                          colorToken="muted"
                        />
                        <BarRounded
                          x={0}
                          y={0}
                          width={width}
                          height={innerHeight}
                          radius={radius}
                          all
                          colorToken={CHART_RAMP_TOKENS[0]}
                        />
                      </>
                    );
                  }}
                </ChartFrame>
              </Box>
              <Box width={28} align="end">
                <Text size="xs" variant="muted">
                  {row.count}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </ScrollArea>
    </Box>
  );
}

function MostActiveUsersBar({ rows, h }: { rows: MostActiveUserRow[]; h: number }) {
  // Users have no entity-owned color (they render avatars, not icons), so every
  // row takes the ramp fallback — through the ONE shared assignment.
  const sliceColors = useCategoricalColors(rows.map((d) => ({ label: yAccessor(d) })));
  const compact = h < 4;
  return (
    <Box direction="column" fill="flex" gap="xs">
      <XYChart<MostActiveUserRow>
        xScale={{ type: "linear", nice: true }}
        yScale={{ type: "band", paddingInner: 0.3, paddingOuter: 0.2 }}
        margin={{ top: 8, right: 12, bottom: compact ? 8 : 28, left: compact ? 8 : 88 }}
        ariaLabel="Most active users"
      >
        {!compact ? <ChartGrid columns rows={false} numTicks={4} /> : null}
        {!compact ? <Axis orientation="bottom" numTicks={4} tickFormat={compactNumber} /> : null}
        {!compact ? <Axis orientation="left" tickFormat={(v) => truncateLabel(String(v), 14)} /> : null}
        <BarSeries
          dataKey="activity"
          data={rows}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={(d) => sliceColors.scale(yAccessor(d))}
        />
        <ChartTooltip
          snapTooltipToDatumY
          showHorizontalCrosshair
          renderTooltip={({ tooltipData }) => {
            const d = tooltipData?.nearestDatum?.datum as MostActiveUserRow | undefined;
            if (!d) return null;
            return (
              <WidgetChartTooltip
                label={yAccessor(d)}
                details={`${d.count} ${d.count === 1 ? "event" : "events"}`}
              />
            );
          }}
        />
      </XYChart>
    </Box>
  );
}

function MostActiveUsersRender({ h, config }: WidgetRenderContext<MostActiveUsersConfig>) {
  const { rows, maxCount, loading } = useMostActiveUsers(config.limit);

  if (loading) return <WidgetSkeleton variant="rows" />;
  if (rows.length === 0) return <WidgetEmpty icon="activity" title="No activity yet" />;

  if (config.mode === "bar") {
    return <MostActiveUsersBar rows={rows} h={h} />;
  }
  return <MostActiveUsersList rows={rows} maxCount={maxCount} />;
}

const mostActiveUsersSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      { key: "limit", kind: "number", label: "Max users", icon: "list", min: 3, max: 20, step: 1 },
    ],
  },
  {
    title: "Display",
    fields: [
      {
        key: "mode",
        kind: "select",
        label: "Mode",
        icon: "bar-chart-3",
        options: [
          { value: "list", label: "List" },
          { value: "bar", label: "Bar chart" },
        ],
      },
    ],
  },
];

export const mostActiveUsersDef: WidgetModule<MostActiveUsersConfig> = {
  type: "most-active-users",
  title: "Most active users",
  icon: "activity",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 10, defaultW: 4, defaultH: 5 },
  configSchema: mostActiveUsersConfigSchema,
  settings: mostActiveUsersSettings,
  catalog: [
    {
      key: "most-active-users.default",
      label: "Most active users",
      description: "Rank members by recent activity events.",
      section: "Charts",
      defaultConfig: { limit: 8, mode: "list" },
      defaultW: 4,
      defaultH: 5,
    },
  ],
  render: MostActiveUsersRender,
};
